import { useState, useEffect } from "react";
import { IoClose } from "react-icons/io5";
import { toast } from "react-hot-toast";
import axios from "axios";
import { useAuthStore } from "../../context/useAuthStore";
import { Link } from "react-router-dom";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL; 

interface BareActModalProps {
  showModal: boolean;
  onClose: () => void;
  subjectName: string;
  subjectCode: string;
}

interface BareAct {
  subjectReferenceActId: string;
  referenceAct: string;
  bareActLink: string;
}

const BareActModal: React.FC<BareActModalProps> = ({
  showModal,
  onClose,
  subjectName,
  subjectCode,
}) => {
  const [bareActs, setBareActs] = useState<BareAct[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const { token, clearSession } = useAuthStore();

  // ✅ Fetch Bare Acts when modal opens
  useEffect(() => {
    if (!showModal) return;

    const fetchBareActs = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await axios.get(
          `${API_BASE_URL}/subjectreference?subjectId=${subjectCode}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        setBareActs(response.data.success || []);
      } catch (err: unknown) {
        setError("Failed to load bare acts.");
        if (axios.isAxiosError(err) && err.response?.status === 401) {
          toast.error("Session expired. Please log in again.");
          clearSession();
        } else {
          toast.error("Error fetching bare acts.");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchBareActs();
  }, [showModal, subjectCode, token, clearSession]);

  if (!showModal) return null;

  return (
    <div className="modalSection">
      <div className="modalContainer">
        <div className="flex justify-between items-center gap-2 border-b pb-1 border-gray-200 mb-2">
          <h2 className="text-lg font-semibold">Bare Acts - {subjectName}</h2>
          <button onClick={onClose} className="modalCloseButton">
            <IoClose size={20} />
          </button>
        </div>

        {loading && <p className="text-left">Loading bare acts...</p>}
        {error && <p className="text-red-500 text-left">{error}</p>}

        {!loading && !error && bareActs.length === 0 && (
          <p className="text-left">No bare acts found for this subject.</p>
        )}

        <div className="mb-4">
          <ul className="list-disc pl-5 space-y-2">
            {bareActs.map((act) => (
              <li key={act.subjectReferenceActId} className="text-left">
                <Link
                  to={act.bareActLink}
                  target="_blank"
                  className="forgotPasswordLink"
                >
                  {act.referenceAct}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default BareActModal;
